import React, { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { useDispatch, useSelector } from "react-redux";
import config from "../../config";
import { setAbstracts, removeAbstract } from "../../slices/abstractSlice";
import { FaEdit, FaTrash, FaPaperPlane, FaPlus } from "react-icons/fa";
import "./abstract.css";

const EditableSection = React.memo(({ label, value, onChange, error }) => {
  return (
    <div className="editable-section">
      <label className="editable-label">{label}</label>
      <div className="editor-toolbar">
        <button type="button" className="toolbar-button">
          <b>B</b>
        </button>
        <button type="button" className="toolbar-button">
          <i>I</i>
        </button>
        <button type="button" className="toolbar-button">
          <u>U</u>
        </button>
        <select className="toolbar-select">
          <option>Font Size</option>
          <option>Small</option>
          <option>Medium</option>
          <option>Large</option>
        </select>
        <select className="toolbar-select">
          <option>Font Family</option>
          <option>Arial</option>
          <option>Times New Roman</option>
          <option>Verdana</option>
          <option>Montserrat</option>
        </select>
      </div>
      <textarea
        className="editor-textarea"
        rows={4}
        value={value}
        onChange={onChange}
      />
      {error && <div className="error-message">{error}</div>}
    </div>
  );
});

const AbstractForm = React.memo(
  ({
    abstractData,
    setAbstractData,
    specialtyOptions,
    subSpecialtyOptions,
    handleSpecialtyChange,
    onSave,
  }) => {
    const [errors, setErrors] = useState({});
    const [fileError, setFileError] = useState("");
    const fileInputRef = useRef(null);

    const handleChange = useCallback(
      (field, value) => {
        setAbstractData((prev) => ({ ...prev, [field]: value }));
        setErrors((prev) => ({ ...prev, [field]: "" }));
      },
      [setAbstractData]
    );

    const handleFileChange = (e) => {
      const file = e.target.files[0];
      if (file) {
        const maxSize = 50 * 1024 * 1024; // 50MB
        if (file.size > maxSize) {
          setFileError("File size exceeds 50MB");
          setAbstractData((prev) => ({ ...prev, file: null }));
          return;
        }
        const allowedExtensions = [
          "jpg",
          "jpeg",
          "png",
          "doc",
          "docx",
          "rtf",
          "txt",
          "pdf",
        ];
        const fileExt = file.name.split(".").pop().toLowerCase();
        if (!allowedExtensions.includes(fileExt)) {
          setFileError("Invalid file type");
          setAbstractData((prev) => ({ ...prev, file: null }));
          return;
        }
        setFileError("");
        setAbstractData((prev) => ({ ...prev, file }));
      }
    };

    const validateForm = () => {
      let newErrors = {};
      if (!abstractData.abstractType)
        newErrors.abstractType = "Please select an abstract type.";
      if (!abstractData.specialty)
        newErrors.specialty = "Please select a specialty.";
      if (!abstractData.subSpecialty)
        newErrors.subSpecialty = "Please select a sub-specialty.";
      if (!abstractData.abstractTitle.trim())
        newErrors.abstractTitle = "Abstract Title is required.";
      setErrors(newErrors);
      return Object.keys(newErrors).length === 0;
    };

    const handleNextClick = () => {
      if (!abstractData.file) {
        setFileError("upload new file");
        return;
      }
      if (validateForm()) {
        onSave();
      }
    };

    return (
      <div className="abstract-container">
        <div className="abstract-header">
          <h2>Abstract</h2>
        </div>
        <div className="abstract-disclaimer">
          File size should be less than 50 MB and name should not exceed 100
          characters. Supported formats: jpg, jpeg, png, doc, docx, rtf, txt,
          pdf.
        </div>
        <div className="form-fields">
          <div className="form-row">
            <label className="form-label">Abstract Type</label>
            <select
              value={abstractData.abstractType}
              onChange={(e) => handleChange("abstractType", e.target.value)}
            >
              <option value="" disabled hidden>
                Select Abstract Type
              </option>
              <option value="Oral">Oral</option>
              <option value="Poster">Poster</option>
              <option value="Workshop">Workshop</option>
            </select>
            {errors.abstractType && (
              <div className="error-message">{errors.abstractType}</div>
            )}
          </div>
          <div className="form-row">
            <label className="form-label">Attach File</label>
            <div className="file-input-wrapper">
              <button
                type="button"
                className="file-choose-btn"
                onClick={() =>
                  fileInputRef.current && fileInputRef.current.click()
                }
              >
                {abstractData.file
                  ? `Selected file: ${abstractData.file.name}`
                  : "Choose file"}
              </button>
              <input
                type="file"
                accept=".jpg,.jpeg,.png,.doc,.docx,.rtf,.txt,.pdf"
                onChange={handleFileChange}
                ref={fileInputRef}
                style={{ display: "none" }}
              />
            </div>
            {fileError && <div className="error-message">{fileError}</div>}
          </div>
          <div className="form-row">
            <label className="form-label">Specialty</label>
            <select
              value={abstractData.specialty}
              onChange={(e) => handleSpecialtyChange(e.target.value)}
            >
              <option value="" disabled hidden>
                Select Specialty
              </option>
              {specialtyOptions.map((opt) => (
                <option key={opt.id} value={opt.Description}>
                  {opt.Description}
                </option>
              ))}
            </select>
            {errors.specialty && (
              <div className="error-message">{errors.specialty}</div>
            )}
          </div>
          <div className="form-row">
            <label className="form-label">Sub-Specialty</label>
            <select
              value={abstractData.subSpecialty}
              onChange={(e) => handleChange("subSpecialty", e.target.value)}
            >
              <option value="" disabled hidden>
                Select Sub-Specialty
              </option>
              {subSpecialtyOptions.map((opt) => (
                <option key={opt.id} value={opt.Description}>
                  {opt.Description}
                </option>
              ))}
            </select>
            {errors.subSpecialty && (
              <div className="error-message">{errors.subSpecialty}</div>
            )}
          </div>
          <EditableSection
            label="Abstract Title"
            value={abstractData.abstractTitle}
            onChange={(e) => handleChange("abstractTitle", e.target.value)}
            error={errors.abstractTitle}
          />
          <EditableSection
            label="Background"
            value={abstractData.background}
            onChange={(e) => handleChange("background", e.target.value)}
            error={errors.background}
          />
          <EditableSection
            label="Material and Method"
            value={abstractData.materialMethod}
            onChange={(e) => handleChange("materialMethod", e.target.value)}
            error={errors.materialMethod}
          />
          <EditableSection
            label="Results"
            value={abstractData.results}
            onChange={(e) => handleChange("results", e.target.value)}
            error={errors.results}
          />
          <EditableSection
            label="Conclusion"
            value={abstractData.conclusion}
            onChange={(e) => handleChange("conclusion", e.target.value)}
            error={errors.conclusion}
          />
        </div>
        <div className="save-button-container">
          <button type="button" className="save-button" onClick={handleNextClick}>
            Next
          </button>
        </div>
      </div>
    );
  }
);

const AbstractReview = React.memo(({ abstractData, onSubmit }) => {
  const { abstractTitle, specialty, subSpecialty, status } = abstractData;
  return (
    <div className="abstract-review-container">
      <h2>Review Your Abstract</h2>
      <div className="table-container">
        <table className="review-table">
          <thead>
            <tr>
              <th>Title</th>
              <th>Theme</th>
              <th>Sub-Type</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>{abstractTitle || "N/A"}</td>
              <td>{specialty || "N/A"}</td>
              <td>{subSpecialty || "N/A"}</td>
              <td>{status || ""}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <div className="submit-button-container">
        <button type="button" className="submit-button" onClick={onSubmit}>
          <FaPaperPlane title="Submit" />
        </button>
      </div>
    </div>
  );
});

const SubmissionConfirmation = React.memo(({ onReturn }) => (
  <div className="submission-confirmation-container">
    <h2>Abstract Submitted</h2>
    <p>
      Your abstract has been submitted. We will get back to you as soon as it is
      evaluated.
    </p>
    <button type="button" className="return-button" onClick={onReturn}>
      Return
    </button>
  </div>
));

const AbstractList = React.memo(
  ({ abstracts, handleRemove, handleEdit, submitAbstract }) => {
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    useEffect(() => {
      const handleResize = () => {
        setIsMobile(window.innerWidth < 768);
      };
      window.addEventListener("resize", handleResize);
      return () => window.removeEventListener("resize", handleResize);
    }, []);

    const isEditEnabled = (abs) =>
      abs.AbstractLevel === 0 && (abs.Status === "" || abs.Status === "Pending");
    const isSubmitEnabled = (abs) =>
      abs.AbstractLevel === 0 && abs.Status === "";

    if (isMobile) {
      return (
        <div className="abstract-list-container">
          {abstracts.map((abs, index) => (
            <div key={abs.pk || index} className="abstract-card">
              <div className="abstract-field">
                <span className="abstract-label">Title:</span>
                <span className="abstract-value">{abs.Title || "N/A"}</span>
              </div>
              <div className="abstract-field">
                <span className="abstract-label">Theme:</span>
                <span className="abstract-value">{abs.Speciality || "N/A"}</span>
              </div>
              <div className="abstract-field">
                <span className="abstract-label">Sub-Type:</span>
                <span className="abstract-value">{abs.SubSpeciality || "N/A"}</span>
              </div>
              <div className="abstract-field">
                <span className="abstract-label">Status:</span>
                <span className="abstract-value">{abs.Status || "Draft"}</span>
              </div>
              <div className="abstract-actions">
                <button
                  type="button"
                  className="icon-button"
                  onClick={() => handleEdit(abs)}
                  disabled={!isEditEnabled(abs)}
                  title="Edit"
                >
                  <FaEdit />
                </button>
                <button
                  type="button"
                  className="icon-button"
                  onClick={() => handleRemove(abs.pk)}
                  title="Remove"
                >
                  <FaTrash />
                </button>
                <button
                  type="button"
                  className="icon-button"
                  onClick={() => submitAbstract(abs)}
                  disabled={!isSubmitEnabled(abs)}
                  title="Submit"
                >
                  <FaPaperPlane />
                </button>
              </div>
            </div>
          ))}
        </div>
      );
    }
    return (
      <div className="abstract-list-container">
        <div className="table-container">
          <table className="abstract-list-table">
            <thead>
              <tr>
                <th style={{ width: "20%" }}>Title</th>
                <th style={{ width: "20%" }}>Theme</th>
                <th style={{ width: "20%" }}>Sub-Theme</th>
                <th style={{ width: "20%" }}>Status</th>
                <th style={{ width: "20%" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {abstracts.map((abs, index) => (
                <tr key={abs.pk || index} style={{ borderBottom: "1px dotted #ddd" }}>
                  <td>{abs.Title || "N/A"}</td>
                  <td>{abs.Speciality || "N/A"}</td>
                  <td>{abs.SubSpeciality || "N/A"}</td>
                  <td>{abs.Status || "Draft"}</td>
                  <td>
                    <button
                      type="button"
                      className="icon-button"
                      onClick={() => handleEdit(abs)}
                      disabled={!isEditEnabled(abs)}
                      title="Edit"
                    >
                      <FaEdit />
                    </button>
                    <button
                      type="button"
                      className="icon-button"
                      onClick={() => handleRemove(abs.pk)}
                      title="Remove"
                    >
                      <FaTrash />
                    </button>
                    <button
                      type="button"
                      className="icon-button"
                      onClick={() => submitAbstract(abs)}
                      disabled={!isSubmitEnabled(abs)}
                      title="Submit"
                    >
                      <FaPaperPlane />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }
);

const AbstractWizard = () => {
  const dispatch = useDispatch();
  const { deviceId, userId, eventId, applicantId, token } = useSelector(
    (state) => state.auth
  );
  const abstractsList = useSelector((state) => state.abstract.abstracts);

  axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;

  const [step, setStep] = useState(0);
  const [abstractData, setAbstractData] = useState({
    abstractType: "",
    specialty: "",
    subSpecialty: "",
    subSpecId: "",
    file: null,
    abstractTitle: "",
    background: "",
    materialMethod: "",
    results: "",
    conclusion: "",
    status: "",
    pk: null,
    abstractLevel: null,
  });
  const [specialtyOptions, setSpecialtyOptions] = useState([]);
  const [subSpecialtyOptions, setSubSpecialtyOptions] = useState([]);
  const [isSubmissionClosed, setIsSubmissionClosed] = useState(false);

  useEffect(() => {
    if (eventId) {
      const url = config.API_LAST_DATE
        .replace("{EventID}", eventId)
        .replace("{typ}", "2");
      axios
        .post(url, {})
        .then((response) => {
          if (
            response.data &&
            response.data.Table &&
            response.data.Table.length > 0 &&
            response.data.Table[0].theDate
          ) {
            const apiDate = new Date(response.data.Table[0].theDate);
            apiDate.setHours(0, 0, 0, 0);
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            if (today > apiDate) {
              setIsSubmissionClosed(true);
            }
          }
        })
        .catch((error) =>
          console.error("Error fetching last submission date:", error)
        );
    }
  }, [eventId]);

  const fetchAbstracts = () => {
    const url = config.API_GET_ABSTRACT_URL
      .replace("{DeviceID}", deviceId)
      .replace("{UserID}", userId)
      .replace("{EventID}", eventId)
      .replace("{ApplicantID}", applicantId);
    return axios
      .get(url)
      .then((response) => {
        if (response.data && response.data.Table) {
          dispatch(setAbstracts(response.data.Table));
          return response.data.Table;
        }
        return [];
      })
      .catch((error) => {
        console.error("Error fetching abstracts:", error);
        return [];
      });
  };

  useEffect(() => {
    fetchAbstracts();
  }, [deviceId, userId, eventId, applicantId, dispatch]);

  const handleRemove = (pk) => {
    const url = config.API_REMOVE_ABSTRACT_URL
      .replace("{DeviceID}", deviceId)
      .replace("{UserID}", userId)
      .replace("{EventID}", eventId)
      .replace("{ApplicantID}", applicantId)
      .replace("{pk}", pk);
    axios
      .post(url)
      .then(() => {
        dispatch(removeAbstract(pk));
        fetchAbstracts();
      })
      .catch((error) => {
        console.error("Error removing abstract:", error);
      });
  };

  useEffect(() => {
    if ((step === 1 || step === 2) && abstractData.pk) {
      const exists = abstractsList.some((abs) => abs.pk === abstractData.pk);
      if (!exists) {
        setStep(0);
        setAbstractData({
          abstractType: "",
          specialty: "",
          subSpecialty: "",
          subSpecId: "",
          file: null,
          abstractTitle: "",
          background: "",
          materialMethod: "",
          results: "",
          conclusion: "",
          status: "",
          pk: null,
          abstractLevel: null,
        });
      }
    }
  }, [abstractsList, abstractData.pk, step]);

  useEffect(() => {
    if (step === 1 && abstractData.pk && abstractData.specialty) {
      const selected = specialtyOptions.find(
        (opt) => opt.Description === abstractData.specialty
      );
      if (selected) {
        const url = config.API_GET_ABSTRACT_SUBSPECIALTY_URL.replace(
          "{SupecialityID}",
          selected.id
        );
        axios
          .post(url)
          .then((response) => {
            if (response.data && response.data.Table) {
              setSubSpecialtyOptions(response.data.Table);
            }
          })
          .catch((error) =>
            console.error("Error fetching subspecialty options:", error)
          );
      }
    }
  }, [step, abstractData.pk, abstractData.specialty, specialtyOptions]);

  useEffect(() => {
    if (step === 1) {
      const url = config.API_GET_ABSTRACT_SPECIALTY_URL.replace(
        "{EventID}",
        eventId
      ).replace("{ApplicantID}", applicantId);
      axios
        .post(url)
        .then((response) => {
          if (response.data && response.data.Table) {
            setSpecialtyOptions(response.data.Table);
          }
        })
        .catch((error) =>
          console.error("Error fetching specialty options:", error)
        );
    }
  }, [step, eventId, applicantId]);

  const handleSpecialtyChange = (value) => {
    setAbstractData((prev) => ({ ...prev, specialty: value, subSpecialty: "" }));
    const selected = specialtyOptions.find((opt) => opt.Description === value);
    if (selected) {
      const url = config.API_GET_ABSTRACT_SUBSPECIALTY_URL.replace(
        "{SupecialityID}",
        selected.id
      );
      axios
        .post(url)
        .then((response) => {
          if (response.data && response.data.Table) {
            setSubSpecialtyOptions(response.data.Table);
          }
        })
        .catch((error) =>
          console.error("Error fetching subspecialty options:", error)
        );
    }
  };

  const handleAddAbstract = () => {
    if (isSubmissionClosed) {
      setStep(4);
      return;
    }
    setAbstractData({
      abstractType: "",
      specialty: "",
      subSpecialty: "",
      subSpecId: "",
      file: null,
      abstractTitle: "",
      background: "",
      materialMethod: "",
      results: "",
      conclusion: "",
      status: "",
      pk: null,
      abstractLevel: null,
    });
    setStep(1);
  };

  const handleEdit = (abstractToEdit) => {
    const mapped = {
      abstractType: abstractToEdit.AbstractType || "",
      specialty: abstractToEdit.Speciality || "",
      subSpecialty: abstractToEdit.SubSpeciality || "",
      subSpecId: "",
      file: null,
      abstractTitle: abstractToEdit.Title || "",
      background: abstractToEdit.BackGround || "",
      materialMethod: abstractToEdit.Material || "",
      results: abstractToEdit.Results || "",
      conclusion: abstractToEdit.Discussion || "",
      status: abstractToEdit.Status || "",
      pk: abstractToEdit.pk,
      abstractLevel: abstractToEdit.AbstractLevel,
    };
    setAbstractData(mapped);
    setStep(1);
  };

  // --- Updated handleSaveAbstract ---
  const handleSaveAbstract = async () => {
    if (!abstractData.file) {
      alert("upload new file");
      return;
    }
    const url = abstractData.pk
      ? config.API_UPDATE_ABSTRACT_URL
      : config.API_SAVE_ABSTRACT_URL;
    const formData = new FormData();
    formData.append("AbstractFile", abstractData.file);
    formData.append("UserID", userId);
    formData.append("DeviceID", deviceId);
    formData.append("EventID", eventId);
    formData.append("ApplicantID", applicantId);
    formData.append("AbstractType", abstractData.abstractType);
    formData.append("Speciality", abstractData.specialty);
    formData.append("SubSpeciality", abstractData.subSpecialty);
    formData.append("SubSpecId", abstractData.subSpecId);
    formData.append("Title", abstractData.abstractTitle);
    formData.append("TitlePlain", abstractData.abstractTitle);
    formData.append("BackGround", abstractData.background);
    formData.append("BackGroundPlain", abstractData.background);
    formData.append("Material", abstractData.materialMethod);
    formData.append("MaterialPlain", abstractData.materialMethod);
    formData.append("Results", abstractData.results);
    formData.append("ResultsPlain", abstractData.results);
    formData.append("Discussion", abstractData.conclusion);
    formData.append("DiscussionPlain", abstractData.conclusion);
    formData.append("pk", abstractData.pk ? abstractData.pk : "0");

    try {
      const response = await axios.post(url, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (
        response.data &&
        response.data.Table &&
        response.data.Table[0].Status === "Success"
      ) {
        const abstracts = await fetchAbstracts();
        if (!abstractData.pk) {
          const newAbs = abstracts.find(
            (abs) =>
              abs.Title === abstractData.abstractTitle &&
              abs.AbstractType.toUpperCase() ===
                abstractData.abstractType.toUpperCase()
          );
          if (newAbs) {
            setAbstractData((prev) => ({ ...prev, pk: newAbs.pk }));
          }
        }
        setStep(2);
      } else {
        alert("Error: Abstract save/update failed");
      }
    } catch (error) {
      console.error("Error saving/updating abstract:", error);
    }
  };

  const submitAbstract = async (abstract) => {
    const url = config.API_SUBMIT_ABSTRACT_URL
      .replace("{DeviceID}", deviceId)
      .replace("{UserID}", userId)
      .replace("{EventID}", eventId)
      .replace("{ApplicantID}", applicantId)
      .replace("{pk}", abstract.pk);
    try {
      await axios.post(url);
      await fetchAbstracts();
      setStep(3);
    } catch (error) {
      console.error("Error submitting abstract:", error);
    }
  };

  const handleSubmitAbstract = () => {
    submitAbstract(abstractData);
  };

  if (step === 4) {
    return (
      <div
        className="abstract-closed-screen"
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <h1>Abstract submission date has passed.</h1>
      </div>
    );
  }

  return (
    <div
      className={`abstract-wizard-container ${
        step === 0 && abstractsList.length === 0 ? "fullscreen" : ""
      }`}
    >
      {step === 0 && abstractsList.length === 0 ? (
        <div className="only-add-abstract-wrapper">
          <button
            type="button"
            onClick={handleAddAbstract}
            className="add-abstract-btn"
          >
            <FaPlus /> Add Abstract
          </button>
        </div>
      ) : (
        <>
          {abstractsList.length > 0 && (
            <AbstractList
              abstracts={abstractsList}
              handleRemove={handleRemove}
              handleEdit={handleEdit}
              submitAbstract={submitAbstract}
            />
          )}
          <div className="add-abstract-btn-container">
            <button
              type="button"
              onClick={handleAddAbstract}
              className="add-abstract-btn"
            >
              <FaPlus /> Add Abstract
            </button>
          </div>
          {step === 1 && (
            <AbstractForm
              abstractData={abstractData}
              setAbstractData={setAbstractData}
              specialtyOptions={specialtyOptions}
              subSpecialtyOptions={subSpecialtyOptions}
              handleSpecialtyChange={handleSpecialtyChange}
              onSave={handleSaveAbstract}
            />
          )}
          {step === 2 && (
            <AbstractReview abstractData={abstractData} onSubmit={handleSubmitAbstract} />
          )}
          {step === 3 && (
            <SubmissionConfirmation onReturn={() => setStep(0)} />
          )}
        </>
      )}
    </div>
  );
};

export default AbstractWizard;
